import { forwardRef, useId, useState, type ReactNode } from "react";

import { ChevronDown } from "@/components/ui/icons";
import { cn } from "@/utils/cn";

// Figma: plain https://www.figma.com/design/HJIp5P6tyeFrUd7ODqeWdM/-PROD--Fertility-V?node-id=4931-5910
// filled https://www.figma.com/design/HJIp5P6tyeFrUd7ODqeWdM/-PROD--Fertility-V?node-id=4809-140356

export const DISCLOSURE_VARIANTS = ["plain", "filled"] as const;

export type DisclosureVariant = (typeof DISCLOSURE_VARIANTS)[number];

type DisclosureProps = {
  /** The always-visible row; the chevron is added to its right. */
  summary: ReactNode;
  /** Shown below the summary while open. */
  children: ReactNode;
  /** plain sits in a divided list; filled is a light creme box of its own. */
  variant?: DisclosureVariant;
  defaultOpen?: boolean;
  /** Layout only. */
  className?: string;
};

const VARIANTS: Record<DisclosureVariant, string> = {
  plain: "",
  filled:
    "rounded-ds-corner-base border border-ds-line-light-on-white bg-ds-background-light-creme px-ds-space-05 py-ds-space-06",
};

export const Disclosure = forwardRef<HTMLButtonElement, DisclosureProps>(
  (
    { summary, children, variant = "plain", defaultOpen = false, className },
    ref
  ) => {
    const [open, setOpen] = useState(defaultOpen);
    const panelId = useId();
    return (
      <div
        className={cn(
          "flex flex-col gap-ds-space-05 md:gap-ds-space-05-desktop",
          VARIANTS[variant],
          className
        )}
      >
        <button
          ref={ref}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            "unset flex w-full cursor-pointer justify-between gap-ds-space-05 text-left text-ds-text-primary",
            variant === "filled"
              ? "items-center ds-body-base-medium"
              : "items-start"
          )}
        >
          <span className="flex min-w-px flex-1 flex-col gap-ds-space-02">
            {summary}
          </span>
          <ChevronDown
            className={cn(
              "shrink-0",
              variant === "plain" && "mt-ds-space-01",
              open && "-scale-y-100"
            )}
          />
        </button>
        <div id={panelId} hidden={!open}>
          {children}
        </div>
      </div>
    );
  }
);
Disclosure.displayName = "Disclosure";
